import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Burger Stack Animations
const stackUp = keyframes`
  0% { transform: translateY(10px) scale(0.8); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-5px); }
  60% { transform: translateY(-3px); }
`;

const sizzle = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.02) rotate(0.5deg); }
  50% { transform: scale(0.98) rotate(-0.5deg); }
  75% { transform: scale(1.01) rotate(0.3deg); }
`;

const HeaderContainer = styled.header`
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(10px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
`;

const LogoIcon = styled.div`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #8B0000, #FFD700);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.1);
  }
`;

const LogoText = styled.div`
  h1 {
    font-size: 1.25rem;
    font-weight: 700;
    color: white;
    margin: 0;
  }
  
  p {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
    
    @media (max-width: 767px) {
      display: none;
    }
  }
`;

// Burger Menu Button with morphing animation
const BurgerMenuButton = styled.button<{ isOpen: boolean }>`
  display: none;
  background: linear-gradient(135deg, #8B0000, #FFD700);
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.3);

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  // Burger layers animation
  .burger-line {
    width: 20px;
    height: 2px;
    background: white;
    margin: 3px 0;
    transition: all 0.3s ease;
    border-radius: 2px;
    position: relative;

    &:nth-child(1) {
      transform: ${props => props.isOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'};
    }

    &:nth-child(2) {
      opacity: ${props => props.isOpen ? '0' : '1'};
      transform: ${props => props.isOpen ? 'translateX(20px)' : 'none'};
    }

    &:nth-child(3) {
      transform: ${props => props.isOpen ? 'rotate(-45deg) translate(7px, -6px)' : 'none'};
    }
  }

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

// Burger Stack Navigation
const BurgerStackNavigation = styled.nav.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  @media (max-width: 767px) {
    position: fixed;
    top: 80px;
    left: 50%;
    transform: translateX(-50%) ${props => props.isOpen ? 'translateY(0)' : 'translateY(-120%)'};
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.95) 0%, rgba(26, 26, 26, 0.98) 100%);
    backdrop-filter: blur(20px);
    border-radius: 25px;
    opacity: ${props => props.isOpen ? '1' : '0'};
    visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    padding: 1.5rem;
    z-index: 999;
    width: 280px;
    border: 2px solid rgba(255, 215, 0, 0.3);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.4),
      0 0 30px rgba(255, 215, 0, 0.2);
  }

  @media (min-width: 768px) {
    display: block;
    position: static;
    background: none;
    padding: 0;
    border: none;
    transform: none;
    opacity: 1;
    visibility: visible;
  }
`;

// Burger Ingredients List
const BurgerIngredientsList = styled.ul`
  display: flex;
  list-style: none;
  gap: 2rem;
  margin: 0;
  padding: 0;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 0;
    width: 100%;
  }
`;

// Individual Burger Layer Item
const BurgerLayer = styled.li<{ delay: number; ingredient: string }>`
  @media (max-width: 767px) {
    animation: ${stackUp} 0.5s ease-out;
    animation-delay: ${props => props.delay * 0.1}s;
    animation-fill-mode: both;
    width: 100%;
    margin: -2px 0;
    
    // Ingredient-specific styling
    background: ${props => {
      switch(props.ingredient) {
        case 'bun-top': return 'linear-gradient(135deg, #D2691E, #CD853F)';
        case 'lettuce': return 'linear-gradient(135deg, #90EE90, #32CD32)';
        case 'cheese': return 'linear-gradient(135deg, #FFD700, #FFC300)';
        case 'franchise': return 'linear-gradient(135deg, #FFD700, #FFA500)';
        case 'patty': return 'linear-gradient(135deg, #8B4513, #A0522D)';
        case 'bun-bottom': return 'linear-gradient(135deg, #D2691E, #CD853F)';
        default: return 'linear-gradient(135deg, #8B0000, #FFD700)';
      }
    }};
    
    border-radius: ${props => {
      switch(props.ingredient) {
        case 'bun-top': return '50px 50px 10px 10px';
        case 'bun-bottom': return '10px 10px 50px 50px';
        default: return '10px';
      }
    }};
    
    position: relative;
    overflow: hidden;
    
    &:hover {
      animation: ${bounce} 0.6s ease-in-out;
      transform: scale(1.02);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    }

    button {
      color: white;
      text-decoration: none;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem 1.5rem;
      position: relative;
      z-index: 2;
      font-size: 1.1rem;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
      background: none;
      border: none;
      width: 100%;
      
      // Ingredient emojis
      &::before {
        content: ${props => {
          switch(props.ingredient) {
            case 'bun-top': return '"🍞"';
            case 'lettuce': return '"🥬"';
            case 'cheese': return '"🧀"';
            case 'patty': return '"🍖"';
            case 'bun-bottom': return '"🍞"';
            case 'franchise': return '"👑"';
            default: return '"👑"';
          }
        }};
        margin-right: 0.75rem;
        font-size: 1.2rem;
        animation: ${sizzle} 2s ease-in-out infinite;
      }

      &:hover {
        color: #ffffff;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
      }
    }
  }

  @media (min-width: 768px) {
    button {
      color: white;
      text-decoration: none;
      font-weight: 500;
      transition: color 0.2s ease;
      cursor: pointer;
      display: block;
      padding: 0.5rem 0;
      background: none;
      border: none;
      width: 100%;
      text-align: left;

      &:hover {
        color: #FFD700;
      }
    }
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const OrderButton = styled.a`
  background: linear-gradient(135deg, #FFD700 0%, #FFC300 100%);
  color: #8B0000;
  text-decoration: none;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  border: 2px solid rgba(255, 215, 0, 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '🍔';
    animation: ${sizzle} 2s ease-in-out infinite;
  }
  
  &:hover {
    background: linear-gradient(135deg, #FFC300 0%, #FFB000 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 215, 0, 0.4);
    border-color: rgba(255, 215, 0, 0.6);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 767px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }
`;

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const menuItems = [
    { id: 'hero', name: 'Home', ingredient: 'bun-top' },
    { id: 'menu', name: 'Menu', ingredient: 'lettuce' },
    { id: 'gallery', name: 'Gallery', ingredient: 'cheese' },
    { id: 'franchise', name: 'Franchise', ingredient: 'franchise' },
    { id: 'about', name: 'About', ingredient: 'patty' },
    { id: 'contact', name: 'Contact', ingredient: 'bun-bottom' }
  ];

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo onClick={() => scrollToSection('hero')}>
          <LogoIcon>
            <img 
              src="/images/cropped-royalewithcheeselogo_Square512.png" 
              alt="Royale with Cheese Logo" 
            />
          </LogoIcon>
          <LogoText>
            <h1>Royale with Cheese</h1>
            <p>Detroit's #1 Burger Destination</p>
          </LogoText>
        </Logo>

        <BurgerStackNavigation isOpen={isMenuOpen}>
          <BurgerIngredientsList className="burger-stack">
            {menuItems.map((item, index) => (
              <BurgerLayer 
                key={item.id}
                delay={index}
                ingredient={item.ingredient}
              >
                <button onClick={() => scrollToSection(item.id)}>{item.name}</button>
              </BurgerLayer>
            ))}
          </BurgerIngredientsList>
        </BurgerStackNavigation>

        <RightSection>
          <OrderButton 
            href="https://online.skytab.com/ebc41ef450819d2886e60b30173a914d/order-settings"
            target="_blank"
            rel="noopener noreferrer"
          >
            Order Now
          </OrderButton>
          <BurgerMenuButton isOpen={isMenuOpen} onClick={toggleMenu}>
            <div className="burger-line"></div>
            <div className="burger-line"></div>
            <div className="burger-line"></div>
          </BurgerMenuButton>
        </RightSection>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 